import {
  accountMutationHeaders,
  readBoundedAccountJson,
} from "./account-state-session-repository.js";
import {
  requiredLegalPublication,
  requireLegalDocuments,
  LEGAL_ACCEPTANCE_SCHEMA_VERSION_V2,
  LEGAL_DOCUMENTS_SCHEMA_VERSION_V1,
  legalDocumentHrefs,
} from "./legal-acceptance-contract.js";

const LEGAL_RESPONSE_MAX_BYTES = 8 * 1024;
const LOCALES = new Set(["en-US", "de-DE"]);

export class LegalAcceptanceContractError extends Error {
  constructor(message) {
    super(message);
    this.name = "LegalAcceptanceContractError";
  }
}

function invalid(message) {
  throw new LegalAcceptanceContractError(message);
}

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactFields(value, expected) {
  if (!isRecord(value)) return false;
  const actual = Object.keys(value).sort();
  const fields = expected.slice().sort();
  return actual.length === fields.length
    && actual.every((field, index) => field === fields[index]);
}

function decodeDescriptor(value, kind, href) {
  if (
    !hasExactFields(value, ["kind", "version", "effectiveDate", "href"])
    || value.kind !== kind
    || typeof value.version !== "string"
    || !/^sha256-[A-Za-z0-9_-]{43}$/.test(value.version)
    || typeof value.effectiveDate !== "string"
    || !/^\d{4}-\d{2}-\d{2}$/.test(value.effectiveDate)
    || value.href !== href
  ) {
    invalid(`Current ${kind} document is invalid.`);
  }
  return Object.freeze({ ...value });
}

function currentLegalDocuments(locale, publication) {
  if (!LOCALES.has(locale)) throw new TypeError("Legal document locale is unsupported.");
  const effective = requireLegalDocuments(publication);
  const hrefs = legalDocumentHrefs(locale);
  return Object.freeze({
    schemaVersion: LEGAL_DOCUMENTS_SCHEMA_VERSION_V1,
    locale,
    terms: Object.freeze({
      kind: "terms",
      version: effective.termsVersion,
      effectiveDate: effective.termsEffectiveDate,
      href: hrefs.terms,
    }),
    privacy: Object.freeze({
      kind: "privacy",
      version: effective.privacyVersion,
      effectiveDate: effective.privacyEffectiveDate,
      href: hrefs.privacy,
    }),
  });
}

function sameDescriptor(left, right) {
  return left.kind === right.kind
    && left.version === right.version
    && left.effectiveDate === right.effectiveDate
    && left.href === right.href;
}

export function decodeCurrentLegalDocumentsV1(value, locale, publication = requiredLegalPublication()) {
  if (!LOCALES.has(locale)) throw new TypeError("Legal document locale is unsupported.");
  if (
    !hasExactFields(value, ["schemaVersion", "locale", "terms", "privacy"])
    || value.schemaVersion !== LEGAL_DOCUMENTS_SCHEMA_VERSION_V1
    || value.locale !== locale
  ) {
    invalid("Current legal documents are invalid.");
  }
  const hrefs = legalDocumentHrefs(locale);
  const terms = decodeDescriptor(value.terms, "terms", hrefs.terms);
  const privacy = decodeDescriptor(value.privacy, "privacy", hrefs.privacy);
  const current = currentLegalDocuments(locale, publication);
  if (!sameDescriptor(terms, current.terms) || !sameDescriptor(privacy, current.privacy)) {
    invalid("The API and this page show different required legal documents. Reload before accepting Terms.");
  }
  return current;
}

export function decodeLegalAcceptanceStatusV2(value, locale, publication = requiredLegalPublication()) {
  if (
    !hasExactFields(value, ["schemaVersion", "current", "accepted"])
    || value.schemaVersion !== LEGAL_ACCEPTANCE_SCHEMA_VERSION_V2
    || typeof value.accepted !== "boolean"
  ) {
    invalid("Legal acceptance status is invalid.");
  }
  const current = decodeCurrentLegalDocumentsV1(value.current, locale, publication);
  return Object.freeze({
    schemaVersion: LEGAL_ACCEPTANCE_SCHEMA_VERSION_V2,
    current,
    accepted: value.accepted,
  });
}

function decodeAcknowledgement(value) {
  if (
    !hasExactFields(value, ["schemaVersion", "accepted"])
    || value.schemaVersion !== LEGAL_ACCEPTANCE_SCHEMA_VERSION_V2
    || value.accepted !== true
  ) {
    invalid("Legal acceptance acknowledgement is invalid.");
  }
  return Object.freeze({
    schemaVersion: LEGAL_ACCEPTANCE_SCHEMA_VERSION_V2,
    accepted: true,
  });
}

export function createLegalAcceptanceRepository({
  resolveApiUrl,
  fetchImpl = globalThis.fetch,
  publication = requiredLegalPublication(),
} = {}) {
  if (typeof resolveApiUrl !== "function" || typeof fetchImpl !== "function") {
    throw new TypeError("Legal acceptance repository dependencies are unavailable.");
  }

  async function documents(locale, { signal } = {}) {
    if (!LOCALES.has(locale)) throw new TypeError("Legal document locale is unsupported.");
    requireLegalDocuments(publication);
    const response = await fetchImpl(resolveApiUrl(
      `/api/v1/legal-documents/current?locale=${encodeURIComponent(locale)}`,
    ), {
      method: "GET", cache: "no-store", credentials: "omit", redirect: "error",
      headers: { Accept: "application/json" }, signal,
    });
    if (!response.ok) throw new Error("Required legal documents are unavailable.");
    return decodeCurrentLegalDocumentsV1(
      await readBoundedAccountJson(response, "Required legal documents", LEGAL_RESPONSE_MAX_BYTES),
      locale, publication,
    );
  }

  async function status(locale, { signal } = {}) {
    if (!LOCALES.has(locale)) throw new TypeError("Legal document locale is unsupported.");
    requireLegalDocuments(publication);
    const response = await fetchImpl(resolveApiUrl(
      `/api/v1/account/legal-acceptance?locale=${encodeURIComponent(locale)}`,
    ), {
      method: "GET",
      cache: "no-store",
      credentials: "include",
      redirect: "error",
      headers: { Accept: "application/json" },
      signal,
    });
    if (!response.ok) throw new Error("Legal acceptance status is unavailable.");
    return decodeLegalAcceptanceStatusV2(
      await readBoundedAccountJson(response, "Legal acceptance", LEGAL_RESPONSE_MAX_BYTES),
      locale,
      publication,
    );
  }

  async function accept(locale, session, { signal } = {}) {
    if (!LOCALES.has(locale)) {
      throw new TypeError("Legal document locale is unsupported.");
    }
    const effective = requireLegalDocuments(publication);
    // Never accept Terms from a browser body that differs from the deployment's selected documents.
    await status(locale, { signal });
    const response = await fetchImpl(resolveApiUrl("/api/v1/account/legal-acceptance"), {
      method: "POST",
      cache: "no-store",
      credentials: "include",
      redirect: "error",
      headers: accountMutationHeaders(session),
      body: JSON.stringify({
        schemaVersion: LEGAL_ACCEPTANCE_SCHEMA_VERSION_V2,
        termsVersion: effective.termsVersion,
        termsAccepted: true,
        locale,
      }),
      signal,
    });
    if (!response.ok) throw new Error("Legal acceptance could not be recorded.");
    return decodeAcknowledgement(await readBoundedAccountJson(
      response,
      "Legal acceptance acknowledgement",
      LEGAL_RESPONSE_MAX_BYTES,
    ));
  }

  return Object.freeze({ documents, status, accept });
}
